<template>
    <div class="position-relative" :id="`goal-${goal.id}`" v-if="goal && goal.domain">
        <div
            class="goal position-relative px-2"
            v-bind:style="getGoalStyle(goal.domain.color)"
            :class="{
                flagged: goal.flagged,
                overlay: actionsOpen,
                'cursor-pointer': canViewGoalDetails,
            }"
        >
            <template v-if="editingGoal">
                <div class="row p-2 justify-content-center">
                    <div class="col-1 p-1 col-sm-1 justify-content-center align-items-center">
                        <select v-model="edited.domain.id" class="form-control form-control-sm">
                            <option v-for="domain in domains" v-bind:value="domain.id">
                                {{ domain.name }}
                            </option>
                        </select>
                    </div>
                    <div class="col-4 p-1 ">
                        <input
                            type="text"
                            v-model="edited.title"
                            :placeholder="goal.title"
                            class="form-control form-control-sm"
                        />
                    </div>
                    <div class="col-4 p-1">
                        <input
                            type="text"
                            v-model="edited.description"
                            :placeholder="goal.description"
                            class="form-control form-control-sm"
                        />
                    </div>
                    <div class="cell col-2 p-1">
                        <div v-if="edited.tracker_type === 'percent'">
                            <span
                                ><input
                                    v-model.number="edited.parameters.success"
                                    type="text"
                                    class="d-inline form-control form-control-sm"
                                    style="max-width: 2rem; line-height: 0.5;"
                            /></span>
                            <em><small class="muted">of</small></em>
                            <span
                                ><input
                                    v-model.number="edited.parameters.attempts"
                                    name="parameter_2"
                                    class="d-inline form-control form-control-sm"
                                    type="text"
                                    style="max-width: 2rem; line-height: 0.5;"
                            /></span>
                        </div>
                        <div v-if="['tally', 'multi_tally'].includes(edited.tracker_type)">
                            <input
                                v-model.number="edited.parameters.success"
                                name="success"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 2rem; line-height: 0.5;"
                            />
                            <em><small class="muted">times</small></em> <br /><em
                                ><small class="muted"> Time limit: </small></em
                            ><br />
                            <time-input v-model="edited.parameters.time_limit"></time-input>
                        </div>
                        <div v-if="edited.tracker_type === 'time_sampling'">
                            <div>
                                <select
                                    class="form-control form-control-sm"
                                    v-model="edited.parameters.method"
                                    name="method"
                                    id="method_ts"
                                >
                                    <option value="" selected disabled hidden :value="null"
                                        >Method</option
                                    >
                                    <option v-for="m in timeSamplingMethods" :value="m.type">
                                        {{ m.description }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <input
                                    v-model.number="edited.parameters.intervals"
                                    v-mask="'##'"
                                    name="seconds"
                                    type="text"
                                    class="d-inline form-control form-control-sm"
                                    style="max-width: 2rem; line-height: 0.5;"
                                />
                                <span class="small">Number of intervals</span>
                            </div>
                            <div>
                                <input
                                    v-model.number="edited.parameters.success"
                                    v-mask="'##'"
                                    name="success"
                                    type="text"
                                    class="d-inline form-control form-control-sm"
                                    style="max-width: 2rem; line-height: 0.5;"
                                />
                                <span class="small">Success</span>
                            </div>
                            <div>
                                <span class="small">Interval length:</span>
                                <time-input
                                    v-model="edited.parameters.interval_length"
                                ></time-input>
                            </div>
                            <div>
                                <span class="small"
                                    >Total time:
                                    {{
                                        moment()
                                            .startOf("day")
                                            .seconds(edited.parameters.interval_length || 0)
                                            .format("H:mm:ss")
                                    }}</span
                                >
                            </div>
                        </div>
                        <div v-if="edited.tracker_type === 'duration'">
                            <time-input v-model="edited.parameters.success"></time-input>
                        </div>
                        <div v-if="edited.tracker_type === 'mand'">
                            <small class="text-muted">Mand: </small
                            ><input
                                v-model.number="edited.parameters.success"
                                name="success"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 3rem; line-height: 0.5;"
                            /><small class="text-muted"> of </small><br />
                            <input
                                v-model.number="edited.parameters.mand_opportunities"
                                name="mand_opp"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 3rem; line-height: 0.5;"
                            /><small class="text-muted">opportunities</small>
                        </div>
                        <div v-if="edited.tracker_type === 'mand_echoic'">
                            <small class="text-muted">Mand: </small
                            ><input
                                v-model.number="edited.parameters.success"
                                name="success"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 3rem; line-height: 0.5;"
                            /><small class="text-muted"> of </small><br />
                            <input
                                v-model.number="edited.parameters.mand_opportunities"
                                name="mand_opp"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 3rem; line-height: 0.5;"
                            /><small class="text-muted">opportunities</small>
                            <div>
                                <small class="text-muted"># repetitions: </small
                                ><input
                                    v-model.number="edited.parameters.echoic_repetitions"
                                    v-mask="'##'"
                                    name="echoic_rpt"
                                    type="text"
                                    class="d-inline form-control form-control-sm"
                                    style="max-width: 3rem; line-height: 0.5;"
                                /><br />
                            </div>
                            <div>
                                <small class="text-muted"># for success: </small
                                ><input
                                    v-model.number="edited.parameters.echoic_repetitions_success"
                                    v-mask="'##'"
                                    name="echoic_rpt"
                                    type="text"
                                    class="d-inline form-control form-control-sm"
                                    style="max-width: 3rem; line-height: 0.5;"
                                />
                            </div>
                        </div>
                        <div v-if="edited.tracker_type === 'task_analysis'">
                            <small class="text-muted">Success: </small
                            ><input
                                v-model.number="edited.parameters.success"
                                name="success"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 3rem; line-height: 0.5;"
                            /><small class="text-muted">%</small>
                        </div>
                        <div v-if="edited.tracker_type === 'cold_probe'">
                            <small class="text-muted">Goal: </small
                            ><input
                                v-model.number="edited.parameters.goal"
                                name="success"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 3rem; line-height: 0.5;"
                            /><small class="text-muted"></small>
                        </div>
                        <div v-if="edited.tracker_type === 'articulation'">
                            <small class="text-muted">Goal: </small
                            ><input
                                v-model.number="edited.parameters.success"
                                name="success"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 3rem; line-height: 0.5;"
                            /><small class="text-muted">%</small>
                        </div>
                        <div v-if="edited.tracker_type === 'cme'">
                            <small class="text-muted">Goal: </small
                            ><input
                                v-model.number="edited.parameters.success"
                                name="success"
                                type="text"
                                class="d-inline form-control form-control-sm"
                                style="max-width: 3rem; line-height: 0.5;"
                            />
                            <span class="d-flex flex-column">
                                <span class="form-check form-check-inline">
                                    <input
                                        class="form-control form-control-sm mr-2"
                                        type="radio"
                                        value="average"
                                        id="cme-avg"
                                        v-model="edited.parameters.evaluate"
                                    />
                                    <label class="mb-0" for="cme-max"
                                        ><small class="text-muted font-italic"
                                            >average</small
                                        ></label
                                    >
                                </span>
                                <span class="form-check form-check-inline">
                                    <input
                                        class="form-control form-control-sm mr-2"
                                        type="radio"
                                        value="max"
                                        id="cme-max"
                                        v-model="edited.parameters.evaluate"
                                    />
                                    <label class="mb-0" for="cme-avg"
                                        ><small class="text-muted font-italic"
                                            >highest</small
                                        ></label
                                    >
                                </span>
                            </span>
                        </div>
                    </div>

                    <div class="cell col-1">
                        <span>
                            <i
                                v-if="!loading"
                                class="fa fa-save pointer-cursor text-primary"
                                @click.stop="updateGoal()"
                            >
                            </i>
                            <i
                                v-if="loading"
                                class="fa fa-spin fa-circle-o-notch text-primary"
                                @click.stop=""
                            >
                            </i>
                            <i
                                class="fa fa-times-circle-o pointer-cursor text-primary"
                                @click.stop="editingGoal = false"
                            >
                            </i>
                        </span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-12">
                        <span
                            class="form-check form-check-inline"
                            id="is_shared"
                            @click="
                                edited.is_shared ? (edited.is_shared = 0) : (edited.is_shared = 1)
                            "
                        >
                            <i
                                dusk="is_shared"
                                class="fa fa-lg fa-fw pr-1 cursor-pointer text-primary"
                                :class="edited.is_shared ? 'fa-check-square' : 'fa-square-o'"
                            ></i>
                            <span class=""
                                >Make this goal shared
                                <i
                                    v-tooltip="
                                        `By making this goal is shared, every therapist assigned to ${
                                            goal.patient.name
                                        } will be able to alter details and parameters, archive or delete this goal.`
                                    "
                                    class="fa fa-lg fa-info-circle text-info"
                                ></i
                            ></span>
                        </span>
                    </div>
                </div>
            </template>

            <div v-else class="row p-2" @click="viewGoalDetails(goal)">
                <div
                    :style="`color: ${goal.domain.color}`"
                    class="col-2 col-sm-1 domain d-flex flex-column justify-content-center align-items-center"
                >
                    {{ goal.domain.abbreviation }}
                </div>
                <div class="cell col-2 col-sm-1 cell" :class="{ 'text-danger': goal.flagged }">
                    {{ moment(goal.data_last_recorded_at).format("MMM DD") }}
                </div>
                <div class="col-4 col-sm-2">
                    <div
                        class="font-weight-bold h-100 d-flex flex-column justify-content-center align-items-start text-left"
                        style="font-size: 0.8rem;"
                    >
                        {{ goal.title }}
                    </div>
                </div>
                <div class="col-6 col-sm-5 cell">
                    {{ goal.description }}
                </div>
                <div class="cell col-9 col-sm-2">
                    <div
                        v-if="
                            goal.tracker_type !== 'CME' &&
                                goal.tracker_type !== 'preference_assessment'
                        "
                        style="width: 100%"
                    >
                        <div class="progress" style="height:0.5rem;">
                            <div
                                class="progress-bar"
                                role="progressbar"
                                :style="
                                    `background-color: ${goal.domain.color}; width: ${
                                        goal.progress
                                    }%`
                                "
                                :aria-valuenow="goal.progress"
                                aria-valuemin="0"
                                aria-valuemax="100"
                            ></div>
                        </div>
                        <small>{{ goal.progress }}%</small>
                    </div>
                    <div v-if="goal.avg">
                        <small>Avg {{ goal.avg }}</small>
                    </div>
                </div>
                <div
                    class="px-2 col-1 col-sm-1 d-flex align-items-center"
                    :class="canAddEditGoals ? 'cursor-pointer' : ''"
                    @click.stop="canAddEditGoals ? openActionsMenu() : null"
                >
                    <profile-picture
                        v-if="goal.created_by_user"
                        :name="goal.created_by_user.name"
                        :size="'sm'"
                        :url="goal.created_by_user.profile_photo_url"
                    ></profile-picture>
                    <span>
                        <i
                            class="fa text-primary ml-2"
                            v-tooltip="goal.is_shared ? 'This is a shared goal' : null"
                            :class="goal.is_shared ? 'fa-users' : 'fa-fw'"
                        ></i>
                    </span>
                    <span v-if="canAddEditGoals || goal.is_shared">
                        <i class="text-primary fa fa-ellipsis-v ml-2 fa-lg cursor-pointer"></i>
                    </span>
                </div>
            </div>
        </div>

        <modal-component
            :id="`modal-goal-${goal.id}`"
            :visible="modal.visible"
            :title="modal.title"
            :size="modal.size"
            @close="resetModal()"
        >
            <div class="list-group" v-if="!selectedAction">
                <button
                    type="button"
                    class="list-group-item list-group-item-action cursor-pointer"
                    @click="editGoal()"
                    @mouseover="$event.target.classList.add('active')"
                    @mouseout="$event.target.classList.remove('active')"
                >
                    Edit
                </button>
                <button
                    v-show="goalStatusFilter < 2"
                    type="button"
                    class="list-group-item list-group-item-action cursor-pointer"
                    @click="selectedAction = 'archive'"
                    @mouseover="$event.target.classList.add('active')"
                    @mouseout="$event.target.classList.remove('active')"
                >
                    Move to Archive
                </button>
                <button
                    v-show="goalStatusFilter >= 2"
                    type="button"
                    class="list-group-item list-group-item-action cursor-pointer"
                    @click="unArchiveGoal()"
                    @mouseover="$event.target.classList.add('active')"
                    @mouseout="$event.target.classList.remove('active')"
                >
                    Move to Current Goals
                </button>
                <button
                    type="button"
                    class="list-group-item list-group-item-action cursor-pointer"
                    @click="selectedAction = 'delete'"
                    @mouseover="$event.target.classList.add('active')"
                    @mouseout="$event.target.classList.remove('active')"
                >
                    Delete Goal
                </button>
            </div>

            <div v-if="selectedAction === 'delete'">
                <p class="">
                    Are you sure you want to delete this goal? All of the data associated with this
                    goal will be permanently removed.
                </p>
                <a
                    class="btn btn-sm btn-primary font-weight-bold text-white text-uppercase btn-block m-1"
                    @click="deleteGoal()"
                    ><small
                        v-html="
                            loading
                                ? `<i class='fa fa-spin fa-circle-o-notch mr-2'></i> Deleting...`
                                : `Yes, I want to permanently delete this goal`
                        "
                    ></small
                ></a>
            </div>

            <div v-if="selectedAction === 'archive'">
                <small class="font-weight-bold text-primary mt-2"
                    >Discontinue and move to archive</small
                >
                <div class="input-group input-group-sm mx-1">
                    <select
                        v-model="archiveReason"
                        class="custom-select custom-select-sm"
                        id="inputGroupSelect04"
                    >
                        <option value="" disabled selected>Reason:</option>
                        <option value="Mastered">Mastered</option>
                        <option value="No longer appropriate">No longer appropriate</option>
                        <option value="On hold">On hold</option>
                        <option value="No opportunity">No opportunity</option>
                        <option value="Barrier to targeting">Barrier to targeting</option>
                        <option value="Other">Other</option>
                    </select>
                    <div class="input-group-append">
                        <button
                            class="btn btn-sm btn-danger text-white font-weight-bold cursor-pointer"
                            :disabled="archiveReason == ''"
                            @click.stop="archiveGoal()"
                            v-html="
                                loading
                                    ? `<i class='fa fa-spin fa-circle-o-notch mr-2'></i> Archiving...`
                                    : `Archive`
                            "
                        ></button>
                    </div>
                </div>
            </div>
        </modal-component>
    </div>
</template>

<script>
import ModalComponent from "../ui/ModalComponent";
import TimeInput from "../ui/TimeInput";
import ProfilePicture from "../ui/ProfilePicture";
import { permissions } from "../../plugins/permissions";

export default {
    name: "GoalSummary",
    props: ["goal", "goalStatusFilter", "actionsOpen", "domains", "userId"],
    mixins: [permissions],
    components: {
        ModalComponent,
        TimeInput,
        ProfilePicture,
    },
    data() {
        return {
            actions: false,
            confirmation: false,
            editingGoal: false,
            edited: {},
            organization: [],
            archiveReason: "",
            loading: false,
            selectedAction: null,
            modal: {
                visible: false,
                size: "sm",
                title: null,
            },
        };
    },
    computed: {
        timeSamplingMethods() {
            return [
                {
                    type: "partial_interval",
                    description: "Partial Interval",
                },
                {
                    type: "whole_interval",
                    description: "Whole Interval",
                },
                {
                    type: "momentary_time_sampling",
                    description: "Momentary Time Sampling",
                },
            ];
        },
    },
    methods: {
        viewGoalDetails(goal) {
            if (this.canViewGoalDetails) {
                this.$emit("set-parent-prop", ["goal", goal]);
            }
        },

        resetModal() {
            this.modal.visible = false;
        },

        resetComponent() {
            this.editingGoal = false;
            this.loading = false;
            this.edited = null;
        },
        getGoalStyle(color) {
            return {
                "-webkit-box-shadow": "inset 4px 0 0 0 " + color,
                "box-shadow": "inset 4px 0 0 0 " + color,
            };
        },
        openActionsMenu() {
            this.selectedAction = null;
            this.modal.visible = true;
            this.modal.title = this.goal.title;
        },
        editGoal() {
            this.resetModal();
            this.edited = { ...this.goal };
            if (_.get(this.edited, "parameters.success", false)) {
                if (typeof this.edited.parameters.success === "string") {
                    this.edited.parameters.success = parseInt(this.edited.parameters.success);
                }
            }
            if (_.get(this.edited, "parameters.attempts", false)) {
                if (typeof this.edited.parameters.attempts === "string") {
                    this.edited.parameters.attempts = parseInt(this.edited.parameters.attempts);
                }
            }
            this.editingGoal = true;
        },
        updateGoal() {
            this.loading = true;
            this.edited.domain_id = this.edited.domain.id;
            axios
                .patch(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`, this.edited)
                .then(response => {
                    this.$emit("refresh-goals");
                    this.resetComponent();
                })
                .catch(e => {
                    if (_.get(e, "response.status", null) === 403) {
                        this.$swal({
                            title: "Error",
                            text: `This goal was created by ${_.get(
                                this.goal,
                                "created_by_user.name",
                                "another user"
                            )}, you are not authorized to edit it. `,
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    } else {
                        this.$swal({
                            title: "Error",
                            text: "An error occurred trying to edit this goal.",
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    }
                    console.error(e);
                    this.resetComponent();
                });
        },
        archiveGoal() {
            this.loading = true;
            axios
                .patch(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`, {
                    is_archived: true,
                    archive_reason: this.archiveReason,
                })
                .then(response => {
                    this.loading = false;
                    this.resetModal();
                    this.$emit("refresh-goals");
                })
                .catch(e => {
                    this.loading = false;
                    console.log(e.response.status);
                    if (_.get(e, "response.status", null) === 403) {
                        this.$swal({
                            title: "Error",
                            text: `This goal was created by ${_.get(
                                this.goal,
                                "created_by_user.name",
                                "another user"
                            )}, you are not authorized to archive it. `,
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    } else {
                        this.$swal({
                            title: "Error",
                            text: "An error occurred trying to archive this goal.",
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    }
                    console.error(e);
                });
        },
        unArchiveGoal() {
            this.loading = true;
            axios
                .patch(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`, {
                    is_archived: false,
                    archive_reason: null,
                })
                .then(response => {
                    this.loading = false;
                    this.resetModal();
                    this.$emit("refresh-goals");
                    this.resetComponent();
                })
                .catch(e => {
                    this.loading = false;
                    if (_.get(e, "response.status", null) === 403) {
                        this.$swal({
                            title: "Error",
                            text: `This goal was created by ${_.get(
                                this.goal,
                                "created_by_user.name",
                                "another user"
                            )}, you are not authorized to unarchive it. `,
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    } else {
                        this.$swal({
                            title: "Error",
                            text: "An error occurred trying to unarchive this goal.",
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    }
                    console.error(e);
                });
        },
        deleteGoal() {
            this.loading = true;
            axios
                .delete(`/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`)
                .then(response => {
                    this.loading = false;
                    this.resetModal();
                    this.$emit("refresh-goals");
                })
                .catch(e => {
                    this.loading = false;
                    if (_.get(e, "response.status", null) === 403) {
                        this.$swal({
                            title: "Error",
                            text: `This goal was created by ${_.get(
                                this.goal,
                                "created_by_user.name",
                                "another user"
                            )}, you are not authorized to delete it. `,
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    } else {
                        this.$swal({
                            title: "Error",
                            text: "An error occurred trying to delete this goal.",
                            type: "error",
                            confirmButtonText: "Close",
                            confirmButtonColor: "#d64571",
                        });
                    }
                    console.error(e);
                });
        },
    },
};
</script>

<style scoped lang="scss">
.overlay {
    opacity: 0.2;
}
.actions {
    z-index: 3;
    width: 280px;
    top: 1rem;
    right: 0;
    left: 0;
    margin: 0 auto;
    border: solid 1px #8f8f8f;
    opacity: 1;
    > a {
        opacity: 1;
    }
}
.cell {
    font-size: 0.8rem;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    //align-items: center;
}
.goal {
    border-bottom: solid 1px #8f8f8f;
    background-color: #fff;
}
.goal.flagged {
    background-color: #fcf8ed;
}
</style>
